import {
    type ElementRef,
    useRef
} from "react";
import {
    useWindowDimensions,
    View
} from "react-native";
import type MemberFiltersProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    SelectBox,
    TextInput,
    Chip
} from "ncore-ui-kit";
import type {
    StatusFilterType
} from "../../type";
import {
    SearchIcon
} from "lucide-react-native";

const MemberFilters = ({
    setSearchQuery,
    searchQuery,
    setFilters,
    filters,
    sectors
}: MemberFiltersProps) => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 992;

    const sectorSelectBoxRef = useRef<ElementRef<typeof SelectBox>>(null);
    const statusSelectBoxRef = useRef<ElementRef<typeof SelectBox>>(null);

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const removeStatusFilter = (status: StatusFilterType) => {
        const nextStatus = filters.status.filter(s => s !== status);
        setFilters(prev => ({
            ...prev,
            status: nextStatus
        }));
        if (statusSelectBoxRef.current) {
            statusSelectBoxRef.current.updateSelections(
                nextStatus.map(st => {
                    let title = "";
                    if (st === "isStudent") title = localize("members-filter-student");
                    if (st === "isBlacklisted") title = localize("members-filter-blacklisted");
                    if (st === "isDeleted") title = localize("members-filter-deleted");
                    return {
                        __title: title,
                        __key: st
                    };
                })
            );
        }
    };

    const removeSectorFilter = (sectorID: string) => {
        const nextSectorIDs = filters.sectorIDs.filter(id => id !== sectorID);
        setFilters(prev => ({
            ...prev,
            sectorIDs: nextSectorIDs
        }));
        if (sectorSelectBoxRef.current) {
            sectorSelectBoxRef.current.updateSelections(
                nextSectorIDs.map(id => {
                    const foundSector = sectors.find(s => s._id === id);
                    const title = foundSector ? foundSector.value : "";
                    return {
                        __title: title,
                        title: title,
                        value: id,
                        __key: id
                    } as never;
                })
            );
        }
    };

    const hasStatus = filters.status.length > 0;
    const hasSector = filters.sectorIDs && filters.sectorIDs.length > 0;

    return <View>
        <View
            style={[
                stylesheet.filterSection,
                {
                    alignItems: isMobile ? "stretch" : "flex-end",
                    flexDirection: isMobile ? "column" : "row",
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.searchContainer,
                    isMobile && {
                        width: "100%"
                    }
                ]}
            >
                <TextInput
                    placeholder={localize("members-search-placeholder")}
                    customColor={colors.project.brandWhite}
                    icon={({
                        color,
                        size
                    }) => <SearchIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    onChangeText={setSearchQuery}
                    spreadBehaviour="stretch"
                    value={searchQuery}
                />
            </View>
            <View
                style={[
                    stylesheet.filterDropdownContainer,
                    {
                        width: isMobile ? "100%" : 180
                    }
                ]}
            >
                <SelectBox
                    onChange={selectedItems => {
                        const statuses = selectedItems.map(item => item.__key as StatusFilterType);
                        setFilters(prev => ({
                            ...prev,
                            status: statuses
                        }));
                    }}
                    data={[
                        {
                            title: localize("members-filter-student"),
                            value: "isStudent"
                        },
                        {
                            title: localize("members-filter-blacklisted"),
                            value: "isBlacklisted"
                        },
                        {
                            title: localize("members-filter-deleted"),
                            value: "isDeleted"
                        }
                    ]}
                    placeholder={localize("members-filter-status")}
                    title={localize("members-filter-status")}
                    customTheme={{
                        themeKey: "events_filter" as never
                    }}
                    titleExtractor={item => item.title}
                    keyExtractor={item => item.value}
                    spreadBehaviour="stretch"
                    ref={statusSelectBoxRef}
                    isMultipleSelect={true}
                    maxChoice={-1}
                />
            </View>
            <View
                style={[
                    stylesheet.filterDropdownContainer,
                    {
                        width: isMobile ? "100%" : 220
                    }
                ]}
            >
                <SelectBox
                    onChange={selectedItems => {
                        const ids = selectedItems.map(item => item.__key as string);
                        setFilters(prev => ({
                            ...prev,
                            sectorIDs: ids
                        }));
                    }}
                    initialSelectedItems={filters.sectorIDs.map(id => {
                        const foundSector = sectors.find(s => s._id === id);
                        const title = foundSector ? foundSector.value : "";
                        return {
                            __title: title,
                            title: title,
                            value: id,
                            __key: id
                        } as never;
                    })}
                    placeholder={localize("members-filter-sector")}
                    key={`sector-filter-${sectors?.length || 0}`}
                    title={localize("members-filter-sector")}
                    customTheme={{
                        themeKey: "events_filter" as never
                    }}
                    titleExtractor={item => item.title}
                    keyExtractor={item => item.value}
                    data={sectors.map(sector => ({
                        title: sector.value,
                        value: sector._id
                    }))}
                    spreadBehaviour="stretch"
                    ref={sectorSelectBoxRef}
                    isMultipleSelect={true}
                    maxChoice={-1}
                />
            </View>
        </View>
        {
            (hasStatus || hasSector) && <View
                style={{
                    marginTop: spaces.spacingMd,
                    gap: spaces.spacingSm,
                    flexDirection: "row",
                    flexWrap: "wrap"
                }}
            >
                {filters.status.map(status => {
                    let title = "";
                    if (status === "isStudent") title = localize("members-filter-student");
                    if (status === "isBlacklisted") title = localize("members-filter-blacklisted");
                    if (status === "isDeleted") title = localize("members-filter-deleted");

                    return <Chip
                        onPress={() => removeStatusFilter(status)}
                        customColor="primary"
                        isClosable={true}
                        title={title}
                        key={status}
                        size="small"
                    />;
                })}
                {hasSector && filters.sectorIDs.map(sectorID => <Chip
                    title={sectors.find(s => s._id === sectorID)?.value || ""}
                    onPress={() => removeSectorFilter(sectorID)}
                    key={`sector-chip-${sectorID}`}
                    customColor="primary"
                    isClosable={true}
                    size="small"
                />)}
            </View>
        }
    </View>;
};

export default MemberFilters;
